import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { BaseActionHandlerService } from '@core/services/action-handler/base-action-handler.service';
import { ContentService } from '@core/services/content.service';
import { PatientService } from '@core/services/patient.service';

import type { MappedRecommendation } from '@features/get-care/models/recommendation.types';
import { AppointmentSchedulingService } from '@features/get-care/services/appointment-scheduling.service';

import { GroupsRenderer } from '@shared/components/groups-renderer/groups-renderer';
import type { ComponentGroup } from '@shared/models/dynamic-layout.types';
import type { DynamicAction } from '@shared/registry';

import { environment } from 'src/environments/environment';

import type { RecommendationCardConfig, RecommendationCardContent } from './recommendation-card.types';

@Component({
  selector: 'app-recommendation-card',
  template: `
    <app-groups-renderer
      [groups]="interpolatedGroups()"
      (componentAction)="onAction($event)"
    />
  `,
  imports: [GroupsRenderer],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService],
})
export class RecommendationCard {
  private readonly baseActionHandler = inject(BaseActionHandlerService);
  private readonly appointmentSchedulingService = inject(AppointmentSchedulingService);
  private readonly patientService = inject(PatientService);
  private readonly contentService = inject(ContentService<RecommendationCardContent>);

  readonly config = input.required<RecommendationCardConfig>();

  private readonly actionHandler = this.baseActionHandler.createScope({
    schedule: (data: unknown) => {
      const patient = this.patientService.patientData();
      if (!patient) return;
      const { patientNotes, chiefConcern, navigateOnSuccess, returnUrl } = this.config();
      return this.appointmentSchedulingService.confirmVisit(
        data as MappedRecommendation,
        patient,
        patientNotes,
        chiefConcern,
        navigateOnSuccess ?? true,
        returnUrl,
      );
    },
    scheduleForLater: (data: unknown) => {
      const patient = this.patientService.patientData();
      if (!patient) return;
      const { patientNotes, chiefConcern, navigateOnSuccess, returnUrl } = this.config();
      return this.appointmentSchedulingService.scheduleForLater(
        data as MappedRecommendation,
        patient,
        patientNotes,
        chiefConcern,
        navigateOnSuccess ?? true,
        returnUrl,
      );
    },
  });

  private readonly rawContent = this.contentService.rawContent;

  private readonly jexlContext = computed<Record<string, unknown>>(() => ({
    rec: this.config().recommendation,
    border: this.config().border !== false,
    skipAVisitKey: environment.workflowKeys.skipAVisit,
  }));

  protected readonly interpolatedGroups = computed<ComponentGroup[]>(() => {
    const content = this.rawContent();
    if (!content?.groups) return [];
    return this.contentService.interpolateGroups(content.groups, this.jexlContext());
  });

  constructor() {
    afterNextRender(() => void this.contentService.loadComponentContent('recommendationCard'));
  }

  protected onAction(event: DynamicAction): void {
    void this.actionHandler.execute(event);
  }
}
